import React, { useRef, useEffect, useId } from "react";
import { Controller } from "react-hook-form";
import {
  TextAreaWrapper,
  TextAreaFieldWrapper,
  StyledTextArea,
  CharacterCount,
  ErrorMessage,
  FooterRow,
} from "./styles";
import { FormTextAreaProps, TextAreaFieldProps } from "./types";

/**
 * Delay before focusing the textarea when autoFocus is enabled.
 * This ensures the DOM has fully rendered and prevents race conditions
 * with form initialization and validation setup.
 */
const AUTOFOCUS_DELAY_MS = 100;

export const FormTextArea = ({
  name,
  control,
  rules,
  disabled = false,
  placeholder = "",
  autoFocus = false,
  minHeight = 150,
  maxLength,
  showCharacterCount = false,
  id,
  required = false,
}: FormTextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textAreaRef.current) {
      const timeoutId = setTimeout(() => {
        textAreaRef.current?.focus();
      }, AUTOFOCUS_DELAY_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [autoFocus]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <TextAreaField
          textAreaRef={textAreaRef}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          placeholder={placeholder}
          minHeight={minHeight}
          maxLength={maxLength}
          showCharacterCount={showCharacterCount}
          id={id}
          required={required}
        />
      )}
    />
  );
};

const TextAreaField = ({
  textAreaRef,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  minHeight,
  maxLength,
  showCharacterCount,
  id,
  required,
}: TextAreaFieldProps) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const showHint = Boolean(showCharacterCount && maxLength);
  const ariaDescribedBy = error ? errorId : showHint ? hintId : undefined;

  const currentLength = value?.toString().length || 0;
  const isNearLimit = maxLength ? currentLength > maxLength * 0.9 : false;
  const isOverLimit = maxLength ? currentLength > maxLength : false;

  return (
    <TextAreaWrapper>
      <TextAreaFieldWrapper>
        <StyledTextArea
          ref={textAreaRef}
          id={fieldId}
          $error={!!error}
          $minHeight={minHeight}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          value={value ?? ""}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-describedby={ariaDescribedBy}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(e.target.value)
          }
        />
      </TextAreaFieldWrapper>

      {(error || (showCharacterCount && maxLength)) && (
        <FooterRow>
          {error ? (
            <ErrorMessage id={errorId} role="alert">
              {error.message}
            </ErrorMessage>
          ) : (
            <div />
          )}
          {showCharacterCount && maxLength && (
            <CharacterCount
              id={hintId}
              $isNearLimit={isNearLimit}
              $isOverLimit={isOverLimit}
            >
              {currentLength} / {maxLength}
            </CharacterCount>
          )}
        </FooterRow>
      )}
    </TextAreaWrapper>
  );
};
